<template>
  <div class="fixed inset-0 z-50"
    :class="isVisible ? 'pointer-events-auto' : 'pointer-events-none'"
    role="dialog"
    aria-modal="true">
    <transition name="fade" enter-active-class="ease-in-out duration-500" leave-active-class="ease-in-out duration-500"
      enter-from-class="opacity-0" enter-to-class="opacity-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="isVisible" class="fixed inset-0 bg-gray-700/80"></div>
    </transition>

    <transition name="slide" enter-active-class="transform transition ease-in-out duration-500 sm:duration-700"
      enter-from-class="translate-x-full" enter-to-class="translate-x-0"
      leave-active-class="transform transition ease-in-out duration-500 sm:duration-700"
      leave-from-class="translate-x-0" leave-to-class="translate-x-full">
      <div v-if="isVisible" class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div class="pointer-events-auto relative w-screen max-w-md">
              <!-- close (unchanged) -->
              <div class="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
                <button @click="close()" type="button"
                  class="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white">
                  <span class="absolute -inset-2.5"></span>
                  <span class="sr-only">Close panel</span>
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <!-- panel -->
              <div class="relative flex h-full flex-col overflow-y-auto bg-[#f9fafb] py-6 shadow-xl px-4">
                <!-- header -->
                <div>
                  <span class="text-lg font-semibold text-gray-900 block">Tjekliste til dit køretøj</span>
                  <span class="text-sm text-gray-600 block">Du skal indsende en tjekliste, før du kan fortsætte.</span>
                  <span class="text-sm text-gray-600 block">Sæt kun kryds hvis der er fejl.</span>
                </div>

                <!-- headings -->
                <div class="mt-4">
                  <div class="grid grid-cols-2 text-xs uppercase tracking-wide text-gray-500">
                    <div class="py-2">Punkt</div>
                    <div class="py-2 text-right">Har fejl</div>
                  </div>
                </div>

                <!-- rows -->
                <div class="mt-1 flex-1 pb-28">
                  <ul class="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white" v-if="items.length">
                    <li v-for="i in items" :key="i.checkpointId" class="grid grid-cols-2 items-center px-3 py-3">
                      <span class="text-sm text-gray-900">{{ i.displayLabel }}</span>
                      <span class="flex justify-end">
                        <input v-model="values[i.keyName]" type="checkbox" class="h-5 w-5 rounded border-gray-300 accent-primary">
                      </span>
                    </li>
                  </ul>

                  <div v-else class="text-sm text-gray-600 p-3">
                    Ingen punkter at udfylde.
                  </div>
                </div>

                <!-- footer inside panel width -->
                <div class="absolute inset-x-0 bottom-0 bg-white/95 backdrop-blur border-t border-gray-200">
                  <div class="px-4 py-3 space-y-3">
                    <div class="flex items-center justify-between pb-2">
                      <span class="text-sm text-gray-700">Send notifikation til kørselsleder</span>
                      <label class="inline-flex items-center cursor-pointer select-none">
                        <input :disabled="!hasAnyFault" v-model="sendAlarm" type="checkbox" class="sr-only peer" />
                        <div
                          class="relative h-6 w-11 rounded-full bg-gray-300 transition-colors
                                peer-checked:bg-primary
                                before:content-[''] before:absolute before:top-0.5 before:left-0.5
                                before:h-5 before:w-5 before:rounded-full before:bg-white
                                before:transition-transform before:duration-200 before:ease-in-out
                                peer-checked:before:translate-x-[20px]">
                        </div>
                      </label>
                    </div>

                    <button
                      type="button"
                      class="w-full rounded-lg bg-primary px-4 py-2 text-white text-sm font-medium hover:opacity-90 active:opacity-95"
                      @click="submit">
                      Indsend tjekliste
                    </button>
                  </div>
                </div>
                <!-- /footer -->
              </div>
              <!-- /panel -->
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'

const props = defineProps<{ isVisible: boolean }>()
const store = useStore()

const close = () => { store.commit('bookingStore/toggleChecklist') }

// source of truth from store
const checklistForm = computed(() => store.getters['bookingStore/checklistForm'] || store.state.bookingStore.checklistForm)
const items = computed(() => (checklistForm.value?.items ?? []) as Array<{ keyName:string; displayLabel:string; checkpointId:number; sortOrder:number; isRequired:boolean }>)

// dynamic values keyed by KeyName
const values = reactive<Record<string, boolean>>({})
const sendAlarm = ref(false)

const hasAnyFault = computed(() => Object.values(values).some(v => v === true))

// initialize values when items change
watch(items, (arr) => {
  // reset map to only current keys
  const next: Record<string, boolean> = {}
  for (const i of arr) next[i.keyName] = values[i.keyName] ?? false
  // replace contents
  for (const k of Object.keys(values)) delete values[k]
  Object.assign(values, next)
}, { immediate: true })

const submit = async () => {
  await store.dispatch('bookingStore/submitChecklistAndContinue', {
    values: { ...values },
    sendAlarm: sendAlarm.value
  })
}

</script>
